'use client';

/**
 * Theme Style Injector
 * ====================
 * Injects dynamic CSS variables based on site configuration.
 * This allows theme colors to be changed from the admin panel at /admin/branding
 *
 * Controls:
 * - Primary/accent colors (red, gold)
 * - Gray palette (backgrounds, text, borders)
 * - All colors used throughout the site
 */

import { useEffect } from 'react';
import { SiteConfig } from '@/lib/config/siteConfig.shared';

interface ThemeStyleInjectorProps {
  config: SiteConfig;
}

// WCAG relative-luminance contrast ratio between a hex foreground and white background.
function contrastOnWhite(hex: string): number {
  const m = hex.match(/^#?([a-f0-9]{6})$/i);
  if (!m) return 21; // unknown → don't downshift
  const rgb = m[1].match(/.{2}/g)!.map((h) => parseInt(h, 16) / 255);
  const lin = rgb.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  const L = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  return 1.05 / (L + 0.05);
}

// Safe text variant of a brand accent for use on white backgrounds.
// If the tenant-configured color fails WCAG AA on white (<4.5:1), we substitute
// a darkened equivalent. The original color is preserved for backgrounds, badges,
// and other non-text contexts via the existing --sb-gold variable.
// Fallback was '#9A7B1F' — a gold-brown that itself only scores 4.02:1 on white,
// so it FAILED the very AA bar this guard exists to enforce (and clashed once the
// brand moved off gold). '#1B3A6B' is the brand navy at 11.27:1.
function safeAccentForText(hex: string, fallback = '#1B3A6B'): string {
  return contrastOnWhite(hex) >= 4.5 ? hex : fallback;
}

export function ThemeStyleInjector({ config }: ThemeStyleInjectorProps) {
  useEffect(() => {
    // Inject theme CSS variables into the document root
    const root = document.documentElement;

    // Primary colors (CTA red)
    root.style.setProperty('--primary-red', config.theme.primaryColor);
    root.style.setProperty('--primary-dark', config.theme.primaryDark);
    root.style.setProperty('--primary-light', config.theme.primaryLight);
    root.style.setProperty('--sb-red', config.theme.primaryColor);
    root.style.setProperty('--sb-red-hover', config.theme.primaryDark);

    // Accent colors (gold). `--sb-gold` is the raw tenant accent (used on
    // backgrounds/badges). `--sb-gold-text` is a contrast-safe variant for
    // foreground text on white — required because a tenant configuring
    // #FFD700 (pure yellow) would otherwise render product prices at
    // 1.40:1 contrast which fails WCAG AA.
    const accent = config.theme.accentColor;
    const accentTextSafe = safeAccentForText(accent);
    root.style.setProperty('--accent-yellow', accent);
    root.style.setProperty('--accent-yellow-hover', config.theme.accentHover);
    root.style.setProperty('--accent-gold', accent);
    root.style.setProperty('--sb-gold', accent);
    root.style.setProperty('--sb-gold-text', accentTextSafe);
    root.style.setProperty('--sb-gold-hover', config.theme.accentHover);
    root.style.setProperty('--heading-gold', accentTextSafe);

    // Secondary color (brand accent background)
    if (config.theme.secondaryColor) {
      root.style.setProperty('--secondary-color', config.theme.secondaryColor);
    }

    // Gray palette - controls all bg-gray-*, text-gray-*, border-gray-* classes
    if (config.theme.grayPalette) {
      const gp = config.theme.grayPalette;
      root.style.setProperty('--theme-gray-50', gp.gray50);
      root.style.setProperty('--theme-gray-100', gp.gray100);
      root.style.setProperty('--theme-gray-200', gp.gray200);
      root.style.setProperty('--theme-gray-300', gp.gray300);
      root.style.setProperty('--theme-gray-400', gp.gray400);
      root.style.setProperty('--theme-gray-500', gp.gray500);
      root.style.setProperty('--theme-gray-600', gp.gray600);
      root.style.setProperty('--theme-gray-700', gp.gray700);
      root.style.setProperty('--theme-gray-800', gp.gray800);
      root.style.setProperty('--theme-gray-900', gp.gray900);
      root.style.setProperty('--theme-gray-950', gp.gray950);

      // Sync with sb-* tokens
      root.style.setProperty('--sb-bg', gp.gray50);
      root.style.setProperty('--sb-bg-alt', gp.gray100);
      root.style.setProperty('--sb-border', gp.gray300);
      root.style.setProperty('--sb-text-muted', gp.gray600);
      root.style.setProperty('--sb-text', gp.gray900);
      root.style.setProperty('--heading-charcoal', gp.gray900);

      // Sync with legacy variables
      root.style.setProperty('--neutral-ivory', gp.gray50);
      root.style.setProperty('--neutral-cream', gp.gray100);
      root.style.setProperty('--neutral-linen', gp.gray200);
      root.style.setProperty('--neutral-gray', gp.gray300);
      root.style.setProperty('--neutral-dark', gp.gray900);
      root.style.setProperty('--bg-main', gp.gray50);
      root.style.setProperty('--bg-ivory', gp.gray50);
      root.style.setProperty('--bg-cream', gp.gray100);
      root.style.setProperty('--bg-linen', gp.gray200);
      root.style.setProperty('--text-default', gp.gray900);
      root.style.setProperty('--text-primary', gp.gray900);
      root.style.setProperty('--text-secondary', gp.gray700);
      root.style.setProperty('--text-muted', gp.gray600);
    }

    // Background colors that use primary (for header/footer if using primary color)
    root.style.setProperty('--bg-footer', config.theme.grayPalette?.gray900 || config.theme.primaryColor);
    root.style.setProperty('--bg-header', '#FFFFFF');
    root.style.setProperty('--bg-header-top', config.theme.grayPalette?.gray900 || config.theme.primaryDark);
    root.style.setProperty('--bg-charcoal', config.theme.grayPalette?.gray900 || '#2E2E2E');

    // Status colors
    root.style.setProperty('--status-info', config.theme.primaryColor);
    root.style.setProperty('--status-warning', config.theme.accentColor);

    // Hover states
    root.style.setProperty('--hover-primary', config.theme.primaryDark);
    root.style.setProperty('--hover-yellow', config.theme.accentHover);
  }, [config.theme]);

  // This component doesn't render anything visible
  return null;
}
